import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useModalAriaHidden from '../../hooks/useModalAriaHidden';
import useQueryParamState from '../../hooks/useQueryParamState';
import '../Dashboard/Dashboard.css';
import './BoothManagement.css';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import PageInstructionBanner from '../common/PageInstructionBanner';
import filterIcon from '../../assets/filter.png';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject as GridInject, Page, Sort, Filter, Toolbar as GridToolbar, Selection, Resize, Reorder, ColumnChooser, ColumnMenu, Freeze } from '@syncfusion/ej2-react-grids';
import { DataUtil } from '@syncfusion/ej2-data';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import { Input, MultiSelect, DateTimePicker, Checkbox, TextArea } from '../UI/FormComponents';
import OpenPositionsEditor from './OpenPositionsEditor';
import { listEvents } from '../../services/events';
import { listBooths, createBooths, deleteBooth, updateBooth, updateBoothRichSections, updateBoothEmployerPageSections, bulkDeleteBooths, getBoothTestLink, createBoothTestLink } from '../../services/booths';
import { uploadBoothLogoToS3, uploadVideoToS3, uploadAudioToS3 } from '../../services/uploads';
import {
  LOGO_ACCEPT,
  getLogoUploadErrorMessage,
  validateLogoFile,
} from '../../utils/logoUpload';
import VideoUploadProgress from '../UI/VideoUploadProgress';
import { 
  RichTextEditorComponent as RTE, 
  Toolbar as RTEToolbar, 
  Link as RteLink, 
  Image as RteImage, 
  HtmlEditor, 
  QuickToolbar,
  Table,
  Video,
  Audio,
  EmojiPicker,
  PasteCleanup,
  Count,
  Resize as RTEResize,
  FormatPainter,
  Inject as RTEInject 
} from '@syncfusion/ej2-react-richtexteditor';
import { RTE_QUICK_TOOLBAR_SETTINGS, getInsertImageSettings, getInsertVideoSettings, getInsertAudioSettings, handleRteKeyDown } from '../../utils/rteConfig';
import { closeRteMediaDialog, isVideoFile, isAudioFile, generateVideoHTML, generateAudioHTML } from '../../utils/rteDialogHelper';
import { MdEdit, MdDelete, MdLink, MdBusiness } from 'react-icons/md';
import EmployerPageTemplate from '../BoothQueue/EmployerPageTemplate';
import { SYNC_GRID_FILTER_SETTINGS, SYNC_GRID_CHECKBOX_COLUMN_PROPS } from '../../utils/syncfusionGridHelpers';
import { hydrateStreamMediaUrls } from '../../utils/videoContentProcessor';
import { normalizeInviteCode, buildBoothQueueUrl, buildEventQueueUrl, buildBoothTestQueueUrl } from '../../utils/inviteLink';
import { isValidCustomLink } from '../../utils/viewEmployerLink';

// The Event Title cell joins a booth's events ("June 25, 2026 …, Aug 12, 2026 …"), and event
// names contain commas themselves, so the grid's text operators can't tell events apart:
// "Starts With Aug 12" misses a booth whose first event is June 25. The column's Filter
// instead lists each event on its own and uses this operator, which matches a booth when
// any of its events is the one picked. loadBooths fills the label → event names map.
const BOOTH_HAS_EVENT_OPERATOR = 'boothhasevent';
const boothEventNamesByLabel = new Map();
const normalizeEventName = (name) => String(name ?? '').trim().toLowerCase();
DataUtil.fnOperators[BOOTH_HAS_EVENT_OPERATOR] = (actual, expected) => {
  const target = normalizeEventName(expected);
  if (!target) return true;
  const names = boothEventNamesByLabel.get(actual) || (actual ? [actual] : []);
  return names.some((name) => normalizeEventName(name) === target);
};

const EMPLOYER_PAGE_SECTION_DEFS = [
  { key: 'about', title: 'About Section' },
  { key: 'program', title: 'Special Program Section' },
  { key: 'video', title: 'Hosted Video Section' },
  { key: 'gallery', title: 'Image Gallery Section' },
  { key: 'jobs', title: 'Open Positions Section' },
  { key: 'benefits', title: 'Benefits Section' },
  { key: 'contact', title: 'Call To Action Section' },
  { key: 'social', title: 'Social Links Section' },
];

const getDefaultEmployerPageSections = () => EMPLOYER_PAGE_SECTION_DEFS.map((section, index) => ({
  key: section.key,
  title: section.title,
  contentHtml: '',
  isActive: true,
  order: index,
}));

const BOOTH_FORM_DRAFT_KEY = 'boothManagement_formDraft';
const BOOTH_FORM_RESTORE_FLAG_KEY = 'boothManagement_restoreDraft';

const normalizeId = (value) => {
  let current = value;
  const visited = new Set();
  for (let i = 0; i < 6; i += 1) {
    if (current === null || current === undefined) return '';
    if (typeof current === 'string') return current.trim();
    if (typeof current === 'number' || typeof current === 'bigint') return String(current).trim();
    if (typeof current === 'object') {
      if (visited.has(current)) return '';
      visited.add(current);
      if (current._id !== undefined && current._id !== current) {
        current = current._id;
        continue;
      }
      if (current.id !== undefined && current.id !== current) {
        current = current.id;
        continue;
      }
    }
    try {
      return String(current).trim();
    } catch (error) {
      return '';
    }
  }
  return '';
};

const normalizeIdArray = (values) => {
  if (!Array.isArray(values)) return [];
  return [...new Set(values.map(normalizeId).filter(Boolean))];
};

const getBoothAssignedEventIds = (booth) => {
  if (!booth) return [];
  const eventIds = Array.isArray(booth.eventIds) ? booth.eventIds : [];
  return normalizeIdArray([...eventIds, booth.eventIdRaw]);
};

// Keep in step with isEventOpenForRecruiterLimit in server/routes/booths.js: the per-event
// recruiter cap only applies to live or upcoming events (demo events count until closed).
const isEventOpenForRecruiterLimit = (event, now = new Date()) => {
  if (!event) return false;
  if (['completed', 'cancelled', 'archived'].includes(event.status)) return false;
  if (event.isDemo) return true;
  return !(event.end && new Date(event.end) < now);
};

const describeRecruiterLimitExceeded = (x) =>
  `${x.name} allows ${x.max} recruiters; other booths already have ${x.existing} and this booth has ${x.adding}`;

const DEFAULT_RECRUITER_UNAVAILABLE_MESSAGE =
  'Recruiters are no longer available for live meetings. This booth is still open — you can leave a message for the recruiters.';

export default function BoothManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Defense-in-depth: Check user role on component mount
  useEffect(() => {
    const allowedRoles = ['Admin', 'AdminEvent', 'GlobalSupport'];
    if (user && !allowedRoles.includes(user.role)) {
      // Redirect unauthorized users to dashboard
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    try {
      const shouldRestore = sessionStorage.getItem(BOOTH_FORM_RESTORE_FLAG_KEY) === '1';
      if (!shouldRestore) return;
      const raw = sessionStorage.getItem(BOOTH_FORM_DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.boothForm) {
        setBoothForm(prev => ({
          ...prev,
          ...parsed.boothForm,
          employerPageSections: Array.isArray(parsed.boothForm.employerPageSections) && parsed.boothForm.employerPageSections.length
            ? parsed.boothForm.employerPageSections
            : getDefaultEmployerPageSections(),
        }));
      }
      setBoothMode(parsed?.boothMode || 'create');
      setEditingBoothId(parsed?.editingBoothId || null);
      sessionStorage.setItem(BOOTH_FORM_RESTORE_FLAG_KEY, '0');
    } catch (error) {
      console.error('Failed to restore booth draft', error);
    }
  }, [location.key]);
  
  // Header uses branding/user from shared AdminHeader

  const [boothMode, setBoothMode] = useState('list'); // 'list' | 'create'
  const [boothSaving, setBoothSaving] = useState(false);
  const [boothForm, setBoothForm] = useState({
    boothName: '',
    boothLogo: '',
    boothLogoAlt: '',
    firstHtml: '',
    secondHtml: '',
    thirdHtml: '',
    waitingAreaMode: 'placeholders',
    employerPageTemplateId: 'default-v1',
    employerPageSections: getDefaultEmployerPageSections(),
    openPositions: [],
    recruitersCount: 1,
    eventIds: [],
    customInviteText: '',
    savedInviteSlug: '',
    expireLinkTime: '',
    enableExpiry: false,
    openTime: '',
    recruiterEndTime: '',
    closeTime: '',
    recruiterUnavailableMessage: DEFAULT_RECRUITER_UNAVAILABLE_MESSAGE,
    companyPage: '',
    joinBoothButtonLink: '',
    viewEmployerButtonLink: ''
  });
  const [booths, setBooths] = useState([]);
  const [boothsTotalCount, setBoothsTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loadingBooths, setLoadingBooths] = useState(false);
  
  // Keep the active search query in the URL (?search=) so it survives navigation
  // and reloads without any browser storage.
  const [activeSearchQuery, setActiveSearchQuery] = useQueryParamState('search', '');
  const [searchTriggerNonce, setSearchTriggerNonce] = useState(0);
  const [previewBooth, setPreviewBooth] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
  const [selectedBooths, setSelectedBooths] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // WCAG 1.3.1 / 2.4.3 — aria-hide background when any modal is open
  useModalAriaHidden(confirmOpen || confirmBulkDeleteOpen);
  const toastRef = useRef(null);
  const gridRef = useRef(null);
  const deleteDialogRef = useRef(null);
  const searchInputRef = useRef(null);
  const [editingBoothId, setEditingBoothId] = useState(null);
  const loadingBoothsRef = useRef(false);
  const loadRequestGenRef = useRef(0); // Generation counter to discard stale API responses
  const loadingEventsRef = useRef(false);
  // RTE image upload helpers
  const rteFirstRef = React.useRef(null);
  const rteSecondRef = React.useRef(null);
  const rteThirdRef = React.useRef(null);

  // Upload progress state for video/audio
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Confirmation of the last queue link copied from the booth form. Stays on screen (and in a
  // status region) until the next copy, rather than vanishing like a toast.
  const [queueLinkCopyStatus, setQueueLinkCopyStatus] = useState('');
  // Test queue link of the booth being edited: { code, boothCode } once loaded, code null when
  // none has been made. Its status line stays until the next action, like the copy status.
  const [testLink, setTestLink] = useState({ code: null, boothCode: '', loading: false });
  const [testLinkStatus, setTestLinkStatus] = useState('');
  // Inline error for the View Employer Button Link field; stays until the admin edits the field.
  const [viewEmployerLinkError, setViewEmployerLinkError] = useState('');
  const viewEmployerLinkInputRef = useRef(null);

  // Base URL from current window location (fallback to production domain if unavailable)
  const baseUrl = (typeof window !== 'undefined' && window.location && window.location.origin)
    ? window.location.origin
    : 'https://abilityjobfair.com';

  // Memoize toolbar settings so Syncfusion RTE doesn't reinitialize on every render
  const rteToolbarSettings = useMemo(() => ({
    type: 'Expand',
    enableFloating: true,
    items: [
      'Undo', 'Redo', '|',
      'Bold', 'Italic', 'Underline', 'StrikeThrough', 'InlineCode', '|',
      'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
      'LowerCase', 'UpperCase', '|',
      'SuperScript', 'SubScript', '|',
      'Formats', 'Alignments', '|',
      'OrderedList', 'UnorderedList', '|',
      'Outdent', 'Indent', '|',
      'CreateLink', 'Image', 'Video', 'Audio', '|',
      'CreateTable', '|',
      'EmojiPicker', '|',
      'ClearFormat', '|',
      'Print', 'FullScreen', '|',
      'SourceCode'
    ]
  }), []);

  /** Add auth header so Syncfusion's built-in uploader can reach POST /api/uploads/rte-image */
  const handleImageUploading = useCallback((args) => {
    const token = localStorage.getItem('token');
    if (args.currentRequest && token) {
      args.currentRequest.setRequestHeader('Authorization', `Bearer ${token}`);
    }
  }, []);

  /** After Syncfusion upload succeeds, replace the blob src with the stable proxy URL from server */
  const handleImageUploadSuccess = useCallback((args) => {
    try {
      const response = JSON.parse(args.e?.currentTarget?.response || '{}');
      if (response.url) {
        // Update file name so Syncfusion constructs the correct URL (path + file.name)
        // Server returns url like '/api/uploads/rte-content/image/<userId>/<uuid>_<file>'
        // With path='/api/uploads/rte-content/', file.name should be the key portion
        if (args.file) {
          const key = response.url.replace(/^\/api\/uploads\/rte-content\//, '');
          args.file.name = key;
        }
        // Also directly set the element src as a safety measure
        if (args.element) {
          args.element.src = response.url;
        }
        // Sync RTE content back to form state after DOM update
        setTimeout(() => {
          setBoothForm(prev => {
            const updates = {};
            try { if (rteFirstRef.current?.inputElement) updates.firstHtml = rteFirstRef.current.inputElement.innerHTML; } catch (e) { /* ignore */ }
            try { if (rteSecondRef.current?.inputElement) updates.secondHtml = rteSecondRef.current.inputElement.innerHTML; } catch (e) { /* ignore */ }
            try { if (rteThirdRef.current?.inputElement) updates.thirdHtml = rteThirdRef.current.inputElement.innerHTML; } catch (e) { /* ignore */ }
            return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
          });
        }, 500);
      }
    } catch (err) {
      console.error('Failed to set image URL from server response:', err);
    }
  }, []);

  /** Handle file upload for video/audio with progress tracking */
  const handleFileUploading = useCallback(async (args, rteRef) => {
    console.log('🎥 File uploading triggered:', args);
    
    const file = args.fileData?.rawFile;
    console.log('📁 File detected:', file, 'Type:', file?.type);
    
    if (!file || !rteRef?.current) {
      console.error('❌ No file or RTE ref', 'file:', file, 'rteRef:', rteRef?.current);
      args.cancel = true;
      return;
    }
    
    const isVideo = isVideoFile(file);
    const isAudio = isAudioFile(file);
    console.log('🔍 File classification - Video:', isVideo, 'Audio:', isAudio);
    
    if (!isVideo && !isAudio) {
      console.error('❌ File is neither video nor audio:', file.type);
      showToast('Please select a video or audio file', 'Error', 4000);
      args.cancel = true;
      return;
    }
    
    // Cancel default upload
    args.cancel = true;
    
    // Close the Syncfusion dialog immediately (before showing progress)
    // Pass the RTE instance so we can close via module dialogObj when available.
    closeRteMediaDialog(rteRef.current);
    
    // Show our custom progress modal
    setUploadingFile(file.name);
    setUploadProgress(0);
    setIsUploading(true);
    
    try {
      let downloadUrl;
      
      const onProgress = (percent) => {
        setUploadProgress(percent);
      };
      
      if (isVideo) {
        console.log('⬆️ Uploading video to S3...');
        const result = await uploadVideoToS3(file, onProgress);
        downloadUrl = result.downloadUrl;
        console.log('✅ Video uploaded, URL:', downloadUrl);
        
        // Insert video with proper attributes
        const videoHTML = generateVideoHTML(downloadUrl, file.type);
        rteRef.current.executeCommand('insertHTML', videoHTML);
        const isMov = (file.name || '').toLowerCase().endsWith('.mov');
        showToast(isMov ? 'Video uploaded. If it doesn\'t play, try converting to MP4.' : 'Video uploaded successfully', 'Success', 3000);
      } else if (isAudio) {
        console.log('⬆️ Uploading audio to S3...');
        const result = await uploadAudioToS3(file, onProgress);
        downloadUrl = result.downloadUrl;
        console.log('✅ Audio uploaded, URL:', downloadUrl);
        
        // Insert audio with proper attributes
        const audioHTML = generateAudioHTML(downloadUrl, file.type);
        rteRef.current.executeCommand('insertHTML', audioHTML);
        showToast('Audio uploaded successfully', 'Success', 2000);
      }
      
    } catch (err) {
      console.error('❌ Media upload failed:', err);
      showToast(isVideo ? 'Failed to upload video' : 'Failed to upload audio', 'Error', 4000);
    } finally {
      // Hide progress modal after a brief delay
      setTimeout(() => {
        setIsUploading(false);
        setUploadingFile(null);
        setUploadProgress(0);
      }, 500);
    }
  }, []);

  // The booth's link code: what the admin typed, else the saved code, else one made from the
  // name (the server adds -2, -3, … on save if another booth already uses it).
  const boothInviteCode = useMemo(() => (
    normalizeInviteCode(boothForm.customInviteText)
    || normalizeInviteCode(boothForm.savedInviteSlug)
    || normalizeInviteCode(boothForm.boothName)
  ), [boothForm.customInviteText, boothForm.savedInviteSlug, boothForm.boothName]);

  // Event options for MultiSelect (loaded dynamically)
  const [eventOptions, setEventOptions] = useState([]);
  const [eventLimits, setEventLimits] = useState({}); // { [eventId]: { maxBooths, maxRecruitersPerEvent, name, slug } }
  const [loadingEvents, setLoadingEvents] = useState(false);

  // The booth link works for every event the booth is in (the server sends each job seeker
  // to the event they are registered for). A booth in several events also gets one link per
  // event, as /queue/<booth>/<event> — a path, not ?event=, so nothing gets lost in a paste.
  const buildQueueLinks = useCallback((boothCode, events) => {
    if (!boothCode) return [];
    const boothLink = {
      key: 'booth',
      label: events.length > 1 ? 'All events' : 'Booth link',
      url: buildBoothQueueUrl(baseUrl, boothCode),
    };
    if (events.length < 2) return [boothLink];
    return [
      boothLink,
      ...events.map((ev) => ({
        key: ev.id,
        label: ev.name || 'Event',
        url: buildEventQueueUrl(baseUrl, boothCode, ev.slug),
      })),
    ];
  }, [baseUrl]);

  const boothQueueLinks = useMemo(() => {
    const events = normalizeIdArray(boothForm.eventIds).map((id) => {
      const opt = (eventOptions || []).find((o) => o.value === id);
      const limits = eventLimits[id] || {};
      return {
        id,
        name: limits.name || (opt?.label || '').replace(/\s•\slimit reached$/, '') || 'Event',
        slug: opt?.slug || limits.slug || '',
      };
    });
    return buildQueueLinks(boothInviteCode, events);
  }, [boothForm.eventIds, boothInviteCode, eventOptions, eventLimits, buildQueueLinks]);

  // Load the saved test link whenever a different booth is opened for editing
  useEffect(() => {
    setTestLinkStatus('');
    if (!editingBoothId) {
      setTestLink({ code: null, boothCode: '', loading: false });
      return undefined;
    }
    let cancelled = false;
    setTestLink({ code: null, boothCode: '', loading: true });
    getBoothTestLink(editingBoothId)
      .then((data) => {
        if (cancelled) return;
        setTestLink({ code: data?.testInviteToken || null, boothCode: data?.customInviteSlug || '', loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        setTestLink({ code: null, boothCode: '', loading: false });
        setTestLinkStatus('Could not load the test link. Reload the page to try again.');
      });
    return () => { cancelled = true; };
  }, [editingBoothId]);

  // Uses the saved booth code: an unsaved change to "Booth link name" isn't live yet
  const boothTestQueueUrl = testLink.code
    ? buildBoothTestQueueUrl(baseUrl, normalizeInviteCode(testLink.boothCode) || boothInviteCode, testLink.code)
    : '';

  const buildQueueLinksForBoothRow = useCallback((row) => {
    // Booths saved before every booth had a code fall back to the name, which the server
    // still resolves.
    const code = normalizeInviteCode(row?.customInviteSlug) || normalizeInviteCode(row?.name) || 'booth';
    const events = (Array.isArray(row?.eventAssignments) ? row.eventAssignments : [])
      .map((a) => ({ id: a.id, name: a.name, slug: a.slug }));
    return buildQueueLinks(code, events);
  }, [buildQueueLinks]);

  // Compute current recruiters per event from loaded booths
  const recruitersByEvent = useMemo(() => {
    const map = {};
    for (const b of booths) {
      const recruitersCount = Number(b.recruitersCount) || 0;
      const assignedEventIds = getBoothAssignedEventIds(b);
      for (const eid of assignedEventIds) {
        if (!eid) continue;
        map[eid] = (map[eid] || 0) + recruitersCount;
      }
    }
    return map;
  }, [booths]);

  // Validate recruiters limit per selected event
  const validateRecruiterLimits = (selectedEventIdsInput = boothForm.eventIds) => {
    const exceeded = [];
    const requestedRecruiters = Number(boothForm.recruitersCount) || 0;
    const selectedEventIds = normalizeIdArray(selectedEventIdsInput);
    const normalizedEditingBoothId = normalizeId(editingBoothId);
    const editing = normalizedEditingBoothId
      ? booths.find(b => normalizeId(b.id) === normalizedEditingBoothId)
      : null;
    const editingRecruiters = Number(editing?.recruitersCount) || 0;
    const editingEventIds = new Set(getBoothAssignedEventIds(editing));
    for (const eid of selectedEventIds) {
      // Ended events don't enforce their cap — mirrors isEventOpenForRecruiterLimit on the server.
      if (!isEventOpenForRecruiterLimit(eventLimits?.[eid])) continue;
      const max = eventLimits?.[eid]?.maxRecruitersPerEvent || 0; // 0 => unlimited
      if (!max) continue;
      // existing recruiters for this event, excluding this booth if editing
      let existing = recruitersByEvent[eid] || 0;
      if (editingEventIds.has(eid)) {
        existing = Math.max(0, existing - editingRecruiters);
      }
      const proposedTotal = existing + requestedRecruiters;
      if (proposedTotal > max) {
        exceeded.push({
          eventId: eid,
          name: eventLimits?.[eid]?.name || eid,
          existing,
          adding: requestedRecruiters,
          max,
        });
      }
    }
    return exceeded;
  };

  const loadEvents = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (loadingEventsRef.current) return;
    
    try {
      loadingEventsRef.current = true;
      setLoadingEvents(true);
      const res = await listEvents({ page: 1, limit: 200 });
      const items = res?.events || [];
      const options = items.map(e => {
        const eventId = normalizeId(e?._id);
        const maxBooths = e?.limits?.maxBooths || 0; // 0 means unlimited
        const current = e?.boothCount || 0;
        const reached = maxBooths > 0 && current >= maxBooths;
        return {
          value: eventId,
          label: reached ? `${e.name} • limit reached` : e.name,
          disabled: reached,
          slug: e?.slug || '',
        };
      });
      setEventOptions(options);
      // capture limits for validation
      const limitsMap = {};
      for (const e of items) {
        const eventId = normalizeId(e?._id);
        if (!eventId) continue;
        limitsMap[eventId] = {
          maxBooths: e?.limits?.maxBooths || 0,
          maxRecruitersPerEvent: e?.limits?.maxRecruitersPerEvent || 0,
          name: e?.name || 'Event',
          slug: e?.slug || '',
          status: e?.status || '',
          isDemo: !!e?.isDemo,
          end: e?.end || null,
        };
      }
      setEventLimits(limitsMap);
    } catch (err) {
      console.error('Failed to load events for booth', err);
      setEventOptions([]);
    } finally {
      loadingEventsRef.current = false;
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Center delete dialog when it opens
  useEffect(() => {
    if (confirmOpen && deleteDialogRef.current) {
      const dialogElement = deleteDialogRef.current.element || deleteDialogRef.current;
      if (dialogElement) {
        // Wait for dialog to render
        setTimeout(() => {
          const dialog = document.querySelector('.bm-delete-dialog.e-dialog');
          if (dialog) {
            dialog.style.position = 'fixed';
            dialog.style.top = '50%';
            dialog.style.left = '50%';
            dialog.style.transform = 'translate(-50%, -50%)';
            dialog.style.margin = '0';
          }
        }, 10);
      }
    }
  }, [confirmOpen]);

  // (Focus management moved into reusable Toast component)

  // Shared cell render for booth date/time columns (expiry, open, recruiter end, close)
  const renderDateTimeCell = (value, emptyText) => {
    let content = <span style={{ color: '#6b7280', fontStyle: 'italic' }}>{emptyText}</span>;
    if (value) {
      const date = new Date(value);
      content = isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleString();
    }
    return (
      <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0' }}>
        {content}
      </div>
    );
  };

  // Grid template functions for custom column renders - using Syncfusion ButtonComponent
  const companyPageTemplate = (props) => {
    if (props.waitingAreaMode === 'employerPage') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '4px 0' }}>
          <ButtonComponent
            cssClass="e-outline e-primary e-small"
            onClick={() => setPreviewBooth(props)}
            style={{
              whiteSpace: 'nowrap',
              padding: '8px 24px',
              paddingLeft: '16px',
              paddingRight: '20px',
              borderWidth: '2px',
              minHeight: '36px'
            }}
          >
            <MdBusiness style={{ marginRight: '8px', verticalAlign: 'middle', flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap' }}>Employer Page</span>
          </ButtonComponent>
        </div>
      );
    }

    if (props.companyPage) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '4px 0' }}>
          <ButtonComponent
            cssClass="e-outline e-primary e-small"
            onClick={() => window.open(props.companyPage, '_blank')}
            style={{
              whiteSpace: 'nowrap',
              padding: '8px 24px',
              paddingLeft: '16px',
              paddingRight: '20px',
              borderWidth: '2px',
              minHeight: '36px'
            }}
          >
            <MdBusiness style={{ marginRight: '8px', verticalAlign: 'middle', flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap' }}>Company Page</span>
          </ButtonComponent>
        </div>
      );
    }
    return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not set</span>;
  };

  const actionsTemplate = (props) => {
    const row = props;
    return (
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <ButtonComponent
          cssClass="e-primary e-small"
          onClick={() => copyInvite(row)}
        >
          <MdLink style={{ marginRight: '4px', verticalAlign: 'middle' }} />
          Invite Link
        </ButtonComponent>
        <ButtonComponent
          cssClass="e-primary e-small"
          onClick={() => startEdit(row)}
        >
          <MdEdit style={{ marginRight: '4px', verticalAlign: 'middle' }} />
          Edit
        </ButtonComponent>
        <ButtonComponent
          cssClass="e-outline e-danger e-small"
          onClick={() => handleDelete(row)}
        >
          <MdDelete style={{ marginRight: '4px', verticalAlign: 'middle' }} />
          Delete
        </ButtonComponent>
      </div>
    );
  };

  const setBoothField = (k, v) => setBoothForm(prev => ({ ...prev, [k]: v }));

  const getLatestRteHtml = useCallback((ref, fallback) => {
    try {
      if (ref?.current?.inputElement) return ref.current.inputElement.innerHTML;
      if (ref?.current?.value != null) return ref.current.value;
    } catch (e) { /* fall through */ }
    return fallback;
  }, []);

  /** Toggle waiting-area mode without discarding the other mode's content. */
  const toggleWaitingAreaMode = useCallback(() => {
    setBoothForm((prev) => {
      const switchingToPlaceholders = prev.waitingAreaMode === 'employerPage';
      // Capture latest placeholder HTML from live RTE instances before they are hidden.
      const firstHtml = switchingToPlaceholders
        ? prev.firstHtml
        : getLatestRteHtml(rteFirstRef, prev.firstHtml);
      const secondHtml = switchingToPlaceholders
        ? prev.secondHtml
        : getLatestRteHtml(rteSecondRef, prev.secondHtml);
      const thirdHtml = switchingToPlaceholders
        ? prev.thirdHtml
        : getLatestRteHtml(rteThirdRef, prev.thirdHtml);
      return {
        ...prev,
        firstHtml,
        secondHtml,
        thirdHtml,
        waitingAreaMode: switchingToPlaceholders ? 'placeholders' : 'employerPage',
        // Keep employer page sections/template intact when switching to placeholders.
        employerPageSections: Array.isArray(prev.employerPageSections) && prev.employerPageSections.length
          ? prev.employerPageSections
          : getDefaultEmployerPageSections(),
        employerPageTemplateId: prev.employerPageTemplateId || 'default-v1',
      };
    });
  }, [getLatestRteHtml]);

  // Syncfusion RTE needs a layout refresh after being revealed from [hidden].
  useEffect(() => {
    if (boothForm.waitingAreaMode === 'employerPage') return undefined;
    const timer = window.setTimeout(() => {
      [rteFirstRef, rteSecondRef, rteThirdRef].forEach((ref) => {
        try {
          ref.current?.refreshUI?.();
        } catch (e) { /* ignore */ }
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [boothForm.waitingAreaMode]);

  const persistBoothDraft = useCallback((nextForm = boothForm, nextMode = boothMode, nextEditingId = editingBoothId) => {
    try {
      sessionStorage.setItem(BOOTH_FORM_DRAFT_KEY, JSON.stringify({
        boothForm: nextForm,
        boothMode: nextMode,
        editingBoothId: nextEditingId || null,
      }));
    } catch (error) {
      console.error('Failed to persist booth draft', error);
    }
  }, [boothForm, boothMode, editingBoothId]);

  const clearBoothDraft = useCallback(() => {
    try {
      sessionStorage.removeItem(BOOTH_FORM_DRAFT_KEY);
      sessionStorage.removeItem(BOOTH_FORM_RESTORE_FLAG_KEY);
    } catch (error) {
      console.error('Failed to clear booth draft', error);
    }
  }, []);

  const openEmployerTemplateEditor = () => {
    const draftForm = {
      ...boothForm,
      waitingAreaMode: 'employerPage',
      employerPageSections: Array.isArray(boothForm.employerPageSections) && boothForm.employerPageSections.length
        ? boothForm.employerPageSections
        : getDefaultEmployerPageSections(),
    };
    persistBoothDraft(draftForm, boothMode, editingBoothId);
    navigate('/boothmanagement/template-editor');
  };

  const onPickBoothLogo = async (file) => {
    if (!file) return;
    const validation = validateLogoFile(file);
    if (!validation.ok) {
      alert(validation.message);
      return;
    }
    try {
      setBoothSaving(true);
      const { downloadUrl } = await uploadBoothLogoToS3(file);
      setBoothField('boothLogo', downloadUrl);
    } catch (e) {
      console.error('Booth logo upload failed', e);
      alert(getLogoUploadErrorMessage(e, 'Failed to upload booth logo'));
    } finally {
      setBoothSaving(false);
    }
  };
  const handleCreateBooth = async (e) => {
    e.preventDefault();
    const orgBoothLimit = Number(user?.organizationId?.limits?.maxBooths || 0);
    const orgBoothLimitReached = orgBoothLimit > 0 && boothsTotalCount >= orgBoothLimit;
    if (!editingBoothId && orgBoothLimitReached) {
      showToast(`Booth limit reached (${orgBoothLimit}). You cannot create more booths.`, 'Error', 5000);
      return;
    }
    const viewEmployerButtonLinkValue = (boothForm.viewEmployerButtonLink || '').trim();
    if (viewEmployerButtonLinkValue && !isValidCustomLink(viewEmployerButtonLinkValue)) {
      setViewEmployerLinkError('Enter a full web address that starts with https://, or a page on this site that starts with /. Or leave it empty to use the employer page.');
      viewEmployerLinkInputRef.current?.focus();
      return;
    }
    setBoothSaving(true);
    try {
      const selectedEventIds = normalizeIdArray(boothForm.eventIds);

      // Client-side recruiters limit validation per event
      const exceeded = selectedEventIds.length > 0 ? validateRecruiterLimits(selectedEventIds) : [];
      if (exceeded.length) {
        if (!editingBoothId) {
          const lines = exceeded.map(x => `• ${describeRecruiterLimitExceeded(x)}`).join('\n');
          showToast(`Max number of recruiters reached for selected event(s):\n\n${lines}`, 'Error', 7000);
          return; // block create; updates are validated by backend as source of truth
        }
      }
      // Process expireLinkTime: convert datetime-local to ISO string, or undefined if disabled
      let expireLinkTimeValue;
      if (boothForm.enableExpiry && boothForm.expireLinkTime) {
        try {
          const date = new Date(boothForm.expireLinkTime);
          if (!isNaN(date.getTime())) {
            expireLinkTimeValue = date.toISOString();
          }
        } catch (e) {
          expireLinkTimeValue = undefined;
        }
      }

      const toIsoOrNull = (localValue) => {
        if (!localValue) return null;
        try {
          const date = new Date(localValue);
          return isNaN(date.getTime()) ? null : date.toISOString();
        } catch (e) {
          return null;
        }
      };
      const openTimeValue = toIsoOrNull(boothForm.openTime);
      const recruiterEndTimeValue = toIsoOrNull(boothForm.recruiterEndTime);
      const closeTimeValue = toIsoOrNull(boothForm.closeTime);

      // Prefer live DOM value so the latest textarea edits are saved even if a
      // Syncfusion click races ahead of the last React state flush.
      const messageFromDom = typeof document !== 'undefined'
        ? document.querySelector('textarea[name="recruiterUnavailableMessage"]')?.value
        : undefined;
      const recruiterUnavailableMessageValue = (
        (typeof messageFromDom === 'string' ? messageFromDom : boothForm.recruiterUnavailableMessage) || ''
      ).trim() || DEFAULT_RECRUITER_UNAVAILABLE_MESSAGE;

      if (openTimeValue && recruiterEndTimeValue && new Date(openTimeValue) > new Date(recruiterEndTimeValue)) {
        showToast('Booth open time must be before or equal to recruiter end time.', 'Error', 5000);
        return;
      }
      if (recruiterEndTimeValue && closeTimeValue && new Date(recruiterEndTimeValue) > new Date(closeTimeValue)) {
        showToast('Recruiter end time must be before or equal to booth close time.', 'Error', 5000);
        return;
      }
      if (openTimeValue && closeTimeValue && new Date(openTimeValue) > new Date(closeTimeValue)) {
        showToast('Booth open time must be before or equal to booth close time.', 'Error', 5000);
        return;
      }

      // Read the latest HTML directly from RTE content areas.
      // Image upload success handlers update the DOM img src attribute directly,
      // which may not trigger a React state update via the RTE change event.
      // This ensures we save the correct server URLs instead of blob: URLs.
      // Placeholders stay mounted (CSS-hidden) in employer-page mode, so DOM reads still work.
      const latestFirstHtml = getLatestRteHtml(rteFirstRef, boothForm.firstHtml);
      const latestSecondHtml = getLatestRteHtml(rteSecondRef, boothForm.secondHtml);
      const latestThirdHtml = getLatestRteHtml(rteThirdRef, boothForm.thirdHtml);
      const employerPageSectionsPayload = (boothForm.employerPageSections || [])
        .map((section, index) => ({
          key: section.key,
          title: section.title || EMPLOYER_PAGE_SECTION_DEFS.find(s => s.key === section.key)?.title || `Section ${index + 1}`,
          contentHtml: section.contentHtml || '',
          contentData: section.contentData ?? null,
          isActive: section.isActive !== false,
          order: typeof section.order === 'number' ? section.order : index,
        }))
        .slice(0, 8);
      const employerAboutHtml = employerPageSectionsPayload.find(section => section.key === 'about')?.contentHtml || '';
      const primaryDescription = boothForm.waitingAreaMode === 'employerPage'
        ? employerAboutHtml
        : latestFirstHtml;

      const openPositionsPayload = (boothForm.openPositions || [])
        .map(position => ({
          title: (position?.title || '').trim(),
          locations: (Array.isArray(position?.locations) ? position.locations : [])
            .map(location => (location || '').trim())
            .filter(Boolean),
        }))
        .filter(position => position.title);

      const customInviteSlug = normalizeInviteCode(boothForm.customInviteText);
      const payload = {
        name: boothForm.boothName,
        description: primaryDescription || '',
        logoUrl: boothForm.boothLogo || undefined,
        logoAltText: boothForm.boothLogoAlt || undefined,
        eventIds: selectedEventIds,
        // Always send a string: undefined is dropped from the JSON body, so a
        // cleared link would never reach the server and the old one would stay.
        companyPage: (boothForm.companyPage || '').trim(),
        recruitersCount: boothForm.recruitersCount || 1,
        expireLinkTime: expireLinkTimeValue,
        openTime: openTimeValue,
        recruiterEndTime: recruiterEndTimeValue,
        closeTime: closeTimeValue,
        recruiterUnavailableMessage: recruiterUnavailableMessageValue,
        customInviteSlug: customInviteSlug || undefined,
        joinBoothButtonLink: boothForm.joinBoothButtonLink || '',
        viewEmployerButtonLink: viewEmployerButtonLinkValue,
        waitingAreaMode: boothForm.waitingAreaMode || 'placeholders',
        employerPageTemplateId: boothForm.employerPageTemplateId || 'default-v1',
        employerPageSections: employerPageSectionsPayload,
        openPositions: openPositionsPayload,
        richSections: [
          { title: 'First Placeholder', contentHtml: latestFirstHtml || '' },
          { title: 'Second Placeholder', contentHtml: latestSecondHtml || '' },
          { title: 'Third Placeholder', contentHtml: latestThirdHtml || '' },
        ],
      };
      if (editingBoothId) {
        // Update base fields including events array
        await updateBooth(editingBoothId, {
          name: payload.name,
          description: payload.description,
          logoUrl: payload.logoUrl,
          logoAltText: payload.logoAltText,
          companyPage: payload.companyPage,
          recruitersCount: payload.recruitersCount,
          expireLinkTime: expireLinkTimeValue,
          openTime: openTimeValue,
          recruiterEndTime: recruiterEndTimeValue,
          closeTime: closeTimeValue,
          recruiterUnavailableMessage: payload.recruiterUnavailableMessage,
          customInviteSlug: payload.customInviteSlug,
          joinBoothButtonLink: payload.joinBoothButtonLink,
          viewEmployerButtonLink: payload.viewEmployerButtonLink,
          waitingAreaMode: payload.waitingAreaMode,
          employerPageTemplateId: payload.employerPageTemplateId,
          openPositions: payload.openPositions,
          events: selectedEventIds, // Send full events array
          eventId: selectedEventIds.length > 0 ? selectedEventIds[0] : undefined, // Backward compat
        });
        // Always persist both waiting-area payloads so toggling modes never
        // drops unsaved employer-page or placeholder content on the server.
        await updateBoothEmployerPageSections(editingBoothId, payload.employerPageSections);
        await updateBoothRichSections(editingBoothId, payload.richSections);
        // Refetch and redirect to list
        await loadBooths();
        await loadEvents();
        setBoothMode('list');
        setEditingBoothId(null);
        clearBoothDraft();
        showToast('Booth updated', 'Success', 2500);
      } else {
        const res = await createBooths(payload);
        const createdFromArray = Array.isArray(res?.created) ? res.created.length : 0;
        const createdFromSingle = res?.booth ? 1 : 0;
        const createdCount = createdFromArray + createdFromSingle;
        const skipped = Array.isArray(res?.skipped) ? res.skipped : [];
        if (skipped.length) {
          console.warn('Some events skipped due to limits:', skipped);
          // Try to resolve event labels from current options
          const skippedList = skipped.map(s => {
            const opt = (eventOptions || []).find(o => o.value === s.eventId);
            const label = opt?.label || s.eventId;
            const reasonRaw = (s.reason || '').toString();
            const isRecruiter = /recruit/i.test(reasonRaw);
            const reasonNormalized = isRecruiter ? 'Recruiter limit reached' : 'Booth limit reached';
            return `• ${label} — ${reasonNormalized}`;
          }).join('\n');
          if (createdCount === 0) {
            showToast(`No booths were created.\n\n${skippedList}`, 'Error', 6000);
          } else {
            showToast(`Booth created for some events, but others were skipped due to limits:\n\n${skippedList}`, 'Warning', 6000);
          }
        } else if (createdCount === 0) {
          // Safety: backend responded but nothing created and no skips array
          showToast('No booths were created.', 'Error', 5000);
        } else {
          showToast('Booth created', 'Success', 2500);
        }
        await loadBooths();
        // Reload events to update booth counts in dropdown
        await loadEvents();
        // Only go back to list if at least one booth was created
        if (createdCount > 0) {
          setBoothMode('list');
          setEditingBoothId(null);
          clearBoothDraft();
        }
        if (createdCount === 0) {
          // Stay on form for user to adjust selections
          setEditingBoothId(null);
        }
      }
    } catch (err) {
      console.error(editingBoothId ? 'Update booth failed' : 'Create booth failed', err);
      
      // Handle specific error cases
      if (err?.response?.status === 409) {
        showToast(err?.response?.data?.message || 'Another booth already uses this link name. Choose a different one.', 'Error', 5000);
        return;
      }
      
      // Extract error message from response
      const errorData = err?.response?.data;
      let errorMessage = editingBoothId ? 'Failed to update booth' : 'Failed to create booth';
      
      if (errorData) {
        if (Array.isArray(errorData.exceededEvents) && errorData.exceededEvents.length > 0) {
          const lines = errorData.exceededEvents
            .map(x => `• ${describeRecruiterLimitExceeded({ ...x, name: x.name || x.eventId })}`)
            .join('\n');
          errorMessage = `Max number of recruiters reached for selected event(s):\n\n${lines}`;
        } else
        // Check for validation errors with details
        if (errorData.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
          const validationErrors = errorData.details
            .map(detail => {
              if (detail.msg) return detail.msg;
              if (detail.path) return `${detail.path}: Invalid value`;
              return 'Validation error';
            })
            .filter(Boolean)
            .join('\n');
          
          if (validationErrors) {
            errorMessage = `Validation failed:\n${validationErrors}`;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }
      
      showToast(errorMessage, 'Error', 7000);
    } finally { setBoothSaving(false); }
  };

  // Keep the (uncontrolled) search input in sync with the ?search= query param,
  // including on mount and when navigating back/forward.
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.value = activeSearchQuery || '';
    }
  }, [activeSearchQuery]);


  const handleSearch = () => {
    const query = (searchInputRef.current?.value || '').trim();
    setActiveSearchQuery(query);
    setSearchTriggerNonce((prev) => prev + 1);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    setActiveSearchQuery('');
    // Bump the nonce so loadBooths is always recreated and re-fetches, even when
    // the active query is already empty (otherwise clearing is a no-op and the
    // grid keeps showing stale results).
    setSearchTriggerNonce((prev) => prev + 1);
    setCurrentPage(1); // Reset to first page when clearing
  };

  const loadBooths = useCallback(async () => {
    // Track this specific request so stale responses can be discarded
    const gen = ++loadRequestGenRef.current;

    try {
      setLoadingBooths(true);
      // When searching, fetch a very large number (10000) to ensure ALL matching records are loaded
      // When not searching, fetch 50 for initial load (grid handles client-side pagination)
      const limit = activeSearchQuery && activeSearchQuery.trim() ? 10000 : 50;
      const params = { page: 1, limit };
      const res = await listBooths(params);
      let items = res?.booths || [];
      setBoothsTotalCount(Number(res?.total || 0));
      
      // Client-side filtering if search query exists
      if (activeSearchQuery && activeSearchQuery.trim()) {
        const searchLower = activeSearchQuery.trim().toLowerCase();
        items = items.filter(b => {
          const name = (b.name || '').toLowerCase();
          // Match legacy single eventId and multi-event `events` array (what the grid shows)
          const eventNames = [
            ...(Array.isArray(b.events) ? b.events.map(e => e?.name || '') : []),
            b.eventId?.name || '',
          ]
            .filter(Boolean)
            .map(n => n.toLowerCase());
          const customInvite = (b.customInviteSlug || '').toLowerCase();
          const companyPage = (b.companyPage || '').toLowerCase();
          return name.includes(searchLower) ||
                 eventNames.some(eventName => eventName.includes(searchLower)) ||
                 customInvite.includes(searchLower) ||
                 companyPage.includes(searchLower);
        });
      }
      
      // Discard results if a newer request has already started
      if (gen !== loadRequestGenRef.current) return;

      // Map to grid rows expected by Syncfusion GridComponent
      boothEventNamesByLabel.clear();
      setBooths(items.map(b => {
        // Get events from both events array and single eventId (legacy)
        const eventsArray = b.events || [];
        const assignmentMap = new Map();
        for (const e of eventsArray) {
          const id = normalizeId(e?._id || e);
          if (!id) continue;
          assignmentMap.set(id, {
            id,
            name: e?.name || 'Event',
            slug: e?.slug || '',
          });
        }
        const legacyEventId = normalizeId(b.eventId?._id || b.eventId);
        if (legacyEventId && !assignmentMap.has(legacyEventId)) {
          assignmentMap.set(legacyEventId, {
            id: legacyEventId,
            name: b.eventId?.name || 'Event',
            slug: b.eventId?.slug || '',
          });
        }
        const eventAssignments = Array.from(assignmentMap.values());
        const eventNames = eventAssignments.map(a => a.name).filter(Boolean);
        const eventIds = eventAssignments.map(a => a.id);
        boothEventNamesByLabel.set(eventNames.join(', '), eventNames);

        return {
          id: b._id,
          name: b.name,
          logo: b.logoUrl,
          logoUrl: b.logoUrl,
          logoAltText: b.logoAltText || '',
          events: eventNames,
          eventAssignments,
          eventName: eventNames.join(', ') || '', // Flattened for filtering
          eventIdRaw: legacyEventId || null, // Keep for backward compat
          eventIds: eventIds, // Full events array
          richSections: b.richSections || [],
          waitingAreaMode: b.waitingAreaMode || 'placeholders',
          employerPageTemplateId: b.employerPageTemplateId || 'default-v1',
          employerPageSections: Array.isArray(b.employerPageSections) && b.employerPageSections.length
            ? b.employerPageSections
            : getDefaultEmployerPageSections(),
          openPositions: Array.isArray(b.openPositions) ? b.openPositions : [],
          customInviteSlug: b.customInviteSlug || '',
          companyPage: b.companyPage || '',
          joinBoothButtonLink: b.joinBoothButtonLink || '',
          viewEmployerButtonLink: b.viewEmployerButtonLink || '',
          customUrl: b.customInviteSlug ? buildBoothQueueUrl(baseUrl, b.customInviteSlug) : '',
          recruitersCount: b.recruitersCount ?? 0,
          expireLinkTime: b.expireLinkTime || null,
          openTime: b.openTime || null,
          recruiterEndTime: b.recruiterEndTime || null,
          closeTime: b.closeTime || null,
          recruiterUnavailableMessage: b.recruiterUnavailableMessage || DEFAULT_RECRUITER_UNAVAILABLE_MESSAGE,
        };
      }));
    } catch (e) {
      if (gen === loadRequestGenRef.current) {
        console.error('Failed to load booths', e);
        setBooths([]);
        setBoothsTotalCount(0);
      }
    } finally { 
      if (gen === loadRequestGenRef.current) {
        setLoadingBooths(false);
      }
    }
  }, [activeSearchQuery, baseUrl, searchTriggerNonce]);

  // Get selected booths from grid
  const getSelectedBoothsFromGrid = useCallback(() => {
    if (!gridRef.current) return [];
    
    try {
      if (typeof gridRef.current.getSelectedRecords === 'function') {
        const selectedRows = gridRef.current.getSelectedRecords();
        return selectedRows.map(row => row.id || row._id).filter(Boolean);
      }
      
      if (typeof gridRef.current.getSelectedRowsData === 'function') {
        const selectedRows = gridRef.current.getSelectedRowsData();
        return selectedRows.map(row => row.id || row._id).filter(Boolean);
      }
      
      return [];
    } catch (error) {
      console.error('Error getting selected rows:', error);
      return [];
    }
  }, []);

  // Debounce the fetch so rapid Search/Clear/filter changes collapse into a single
  // request. Direct loadBooths() calls (delete, save, etc.) remain immediate.
  useEffect(() => {
    const t = setTimeout(() => { loadBooths(); }, 250);
    return () => clearTimeout(t);
  }, [loadBooths]);

  // Memoize the current page slice so the grid dataSource keeps a stable reference
  // across unrelated re-renders (selection, hover) and only recomputes on data/page change.
  const paginatedBooths = useMemo(
    () => booths.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [booths, currentPage, pageSize]
  );

  // Event Title column Filter: a labelled native <select> of individual event names in place
  // of the free-text box (whose suggestions were whole joined cells), filtered with
  // BOOTH_HAS_EVENT_OPERATOR. The operator row is hidden because the match is always
  // "booth has this event".
  const boothsRef = useRef(booths);
  boothsRef.current = booths;
  const eventColumnSelectRef = useRef(null);
  const eventColumnFilter = useMemo(() => ({
    ui: {
      create: (args) => {
        const operatorRow = args.dialogObj?.element?.querySelector('.e-flm_optrdiv');
        if (operatorRow) operatorRow.style.display = 'none';

        const names = new Map();
        for (const booth of boothsRef.current) {
          for (const name of booth.events || []) {
            const key = normalizeEventName(name);
            if (key && !names.has(key)) names.set(key, name.trim());
          }
        }
        const sortedNames = [...names.values()].sort((a, b) => a.localeCompare(b));

        const selectId = `bm-event-column-filter-${args.column.uid}`;
        const label = document.createElement('label');
        label.htmlFor = selectId;
        label.className = 'bm-event-column-filter-label';
        label.textContent = 'Show booths in event';
        const select = document.createElement('select');
        select.id = selectId;
        // e-flmenu-input lets the grid move focus here when the Filter menu opens.
        select.className = 'e-flmenu-input bm-event-column-filter-select';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select an event';
        select.appendChild(placeholder);
        for (const name of sortedNames) {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          select.appendChild(option);
        }
        args.target.appendChild(label);
        args.target.appendChild(select);
        eventColumnSelectRef.current = select;
      },
      write: (args) => {
        const select = eventColumnSelectRef.current;
        if (!select) return;
        const current = normalizeEventName(args.filteredValue);
        const match = [...select.options].find((o) => o.value && normalizeEventName(o.value) === current);
        select.value = match ? match.value : '';
      },
      read: (args) => {
        const value = eventColumnSelectRef.current?.value || '';
        if (!value) {
          args.fltrObj.removeFilteredColsByField(args.column.field);
          return;
        }
        args.fltrObj.filterByColumn(args.column.field, BOOTH_HAS_EVENT_OPERATOR, value);
      },
    },
  }), []);

  // Syncfusion Grid does not reliably pick up dataSource prop changes. Calling
  // refresh() alone only re-renders the grid's *existing* internal dataSource, so
  // searched/filtered results only appeared after a full page reload remounted the
  // grid. Assigning the new array to the grid instance forces EJ2 to rebind to the
  // fresh data (which also re-renders templated cells).
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    grid.dataSource = paginatedBooths;
  }, [paginatedBooths]);

  // Track selection changes from grid
  useEffect(() => {
    if (!gridRef.current) return;
    
    const updateSelection = () => {
      const currentSelection = getSelectedBoothsFromGrid();
      setSelectedBooths(currentSelection);
    };

    // Listen for selection events
    const grid = gridRef.current;
    if (grid.element) {
      const handleSelectionChange = () => {
        setTimeout(updateSelection, 100);
      };
      
      grid.element.addEventListener('click', handleSelectionChange);
      
      return () => {
        if (grid.element) {
          grid.element.removeEventListener('click', handleSelectionChange);
        }
      };
    }
  }, [booths, getSelectedBoothsFromGrid]);

  // Set CSS variable for filter icon and make it trigger column menu
  useEffect(() => {
    if (!gridRef.current) return;
    
    const filterIconUrl = `url(${filterIcon})`;
    
    // Set CSS variable on document root
    document.documentElement.style.setProperty('--filter-icon-url', filterIconUrl);
    
    const grid = gridRef.current;
    
    // Override filter icon click to open column menu instead
    const handleFilterIconClick = (e) => {
      const filterIcon = e.target.closest('.e-filtericon');
      if (!filterIcon) return;
      
      e.stopPropagation();
      e.preventDefault();
      
      const headerCell = filterIcon.closest('.e-headercell');
      if (!headerCell || !grid.columnMenuModule) return;
      
      // Get column field from header cell
      const columnIndex = Array.from(headerCell.parentElement.children).indexOf(headerCell);
      const column = grid.columns[columnIndex];
      
      if (column) {
        // Open column menu
        grid.columnMenuModule.openColumnMenu(headerCell, column, e);
      }
    };
    
    // The filter-icon image is styled entirely via CSS (--filter-icon-url), so no
    // per-mutation DOM observer is needed. We only intercept clicks on the grid
    // element to open the column menu. Re-runs on data change so the handler is
    // re-attached if the grid remounts (e.g. list/form mode toggle).
    const gridElement = grid.element;
    if (gridElement) {
      gridElement.addEventListener('click', handleFilterIconClick, true);
    }
    
    return () => {
      document.documentElement.style.removeProperty('--filter-icon-url');
      if (gridElement) {
        gridElement.removeEventListener('click', handleFilterIconClick, true);
      }
    };
  }, [booths]);

  // Frozen columns rely on native Syncfusion movable/frozen pane scrolling.
  useEffect(() => undefined, [booths]);

  // Helper to format ISO date to datetime-local input format (YYYY-MM-DDTHH:mm)
  const formatDateTimeLocal = (isoDateStr) => {
    if (!isoDateStr) return '';
    try {
      const date = new Date(isoDateStr);
      if (isNaN(date.getTime())) return '';
      // Format to local time for datetime-local input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  // Edit handler (basic prefill)
  const startEdit = (row) => {
    // Prefer the full record from state — Syncfusion row props can omit fields
    // that are not declared as grid columns.
    const rowId = normalizeId(row?.id || row?._id);
    const fullRow = booths.find((b) => normalizeId(b.id || b._id) === rowId) || row;
    const expireTime = formatDateTimeLocal(fullRow.expireLinkTime);
    // Use the full eventIds array if available, otherwise fallback to eventIdRaw
    const eventIdsToUse = normalizeIdArray(
      fullRow.eventIds && fullRow.eventIds.length > 0
        ? fullRow.eventIds
        : (fullRow.eventIdRaw ? [fullRow.eventIdRaw] : boothForm.eventIds)
    );
    
    setBoothForm(prev => ({
      ...prev,
      boothName: fullRow.name || '',
      boothLogo: fullRow.logo || '',
      boothLogoAlt: fullRow.logoAltText || '',
      firstHtml: fullRow.richSections?.[0]?.contentHtml || '',
      secondHtml: fullRow.richSections?.[1]?.contentHtml || '',
      thirdHtml: fullRow.richSections?.[2]?.contentHtml || '',
      waitingAreaMode: fullRow.waitingAreaMode || 'placeholders',
      employerPageTemplateId: fullRow.employerPageTemplateId || 'default-v1',
      employerPageSections: Array.isArray(fullRow.employerPageSections) && fullRow.employerPageSections.length
        ? EMPLOYER_PAGE_SECTION_DEFS.map((section, index) => {
          const saved = fullRow.employerPageSections.find(s => s.key === section.key);
          return {
            key: section.key,
            title: saved?.title || section.title,
            contentHtml: saved?.contentHtml || '',
            contentData: saved?.contentData ?? null,
            isActive: saved?.isActive !== false,
            order: typeof saved?.order === 'number' ? saved.order : index,
          };
        })
        : getDefaultEmployerPageSections(),
      openPositions: Array.isArray(fullRow.openPositions)
        ? fullRow.openPositions.map(position => ({
          title: position?.title || '',
          locations: Array.isArray(position?.locations) ? position.locations.filter(Boolean) : [],
        }))
        : [],
      eventIds: eventIdsToUse,
      companyPage: fullRow.companyPage || '',
      customInviteText: fullRow.customInviteSlug || '',
      savedInviteSlug: fullRow.customInviteSlug || '',
      joinBoothButtonLink: fullRow.joinBoothButtonLink || '',
      viewEmployerButtonLink: fullRow.viewEmployerButtonLink || '',
      recruitersCount: fullRow.recruitersCount || 1,
      expireLinkTime: expireTime,
      enableExpiry: !!fullRow.expireLinkTime,
      openTime: formatDateTimeLocal(fullRow.openTime),
      recruiterEndTime: formatDateTimeLocal(fullRow.recruiterEndTime),
      closeTime: formatDateTimeLocal(fullRow.closeTime),
      recruiterUnavailableMessage: fullRow.recruiterUnavailableMessage || DEFAULT_RECRUITER_UNAVAILABLE_MESSAGE,
    }));
    setQueueLinkCopyStatus('');
    setViewEmployerLinkError('');
    setBoothMode('create');
    setEditingBoothId(normalizeId(row.id));
  };

  // Delete handlers
  const handleDelete = (row) => { setRowPendingDelete(row); setConfirmOpen(true); };
  const confirmDelete = async () => {
    if (!rowPendingDelete) return;
    try {
      setBoothSaving(true);
      await deleteBooth(rowPendingDelete.id);
      await loadBooths();
      showToast('Booth deleted', 'Success');
    } catch (e) {
      console.error('Delete booth failed', e);
      showToast('Failed to delete');
    } finally {
      setBoothSaving(false);
      setConfirmOpen(false);
      setRowPendingDelete(null);
    }
  };
  const cancelDelete = () => { setConfirmOpen(false); setRowPendingDelete(null); };

  const handleBulkDelete = () => {
    const currentSelection = getSelectedBoothsFromGrid();
    if (currentSelection.length === 0) {
      showToast('Please select booths to delete', 'Warning');
      return;
    }
    setSelectedBooths(currentSelection);
    setConfirmBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await bulkDeleteBooths(selectedBooths);
      showToast(response.message || 'Booths deleted successfully', 'Success');
      setSelectedBooths([]);
      await loadBooths();
    } catch (error) {
      console.error('Error deleting booths:', error);
      showToast(error.response?.data?.message || 'Failed to delete booths', 'Error');
    } finally {
      setIsDeleting(false);
      setConfirmBulkDeleteOpen(false);
    }
  };

  const cancelBulkDelete = () => {
    setConfirmBulkDeleteOpen(false);
    setSelectedBooths([]);
  };

  // Invite link copy - Syncfusion Toast
  const showToast = (message, type = 'Success', duration = 3000) => {
    if (toastRef.current) {
      // Errors stay until dismissed (WCAG 2.2.1); timeOut 0 keeps a Syncfusion toast open.
      const isError = type === 'Error';
      toastRef.current.show({
        title: type,
        content: message,
        cssClass: `e-toast-${type.toLowerCase()}`,
        showProgressBar: !isError,
        showCloseButton: isError,
        timeOut: isError ? 0 : duration
      });
    }
  };
  const copyInvite = async (row) => {
    const links = buildQueueLinksForBoothRow(row);
    const text = links.length === 1
      ? links[0].url
      : links.map((l) => `${l.label}: ${l.url}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      showToast(
        links.length > 1 ? `${links.length} invite links copied` : 'Invite link copied',
        'Success'
      );
    } catch (e) {
      window.prompt('Copy to clipboard: Ctrl+C, Enter', text);
      showToast('Copy failed. Link shown.');
    }
  };

  const makeTestLink = async () => {
    if (!editingBoothId) return;
    const replacing = !!testLink.code;
    setTestLink((prev) => ({ ...prev, loading: true }));
    try {
      const data = await createBoothTestLink(editingBoothId);
      setTestLink({ code: data?.testInviteToken || null, boothCode: data?.customInviteSlug || '', loading: false });
      setTestLinkStatus(replacing
        ? 'Made a new test link. The previous test link no longer works.'
        : 'Made the test link. Copy it and share it with the people testing this booth.');
    } catch (e) {
      setTestLink((prev) => ({ ...prev, loading: false }));
      setTestLinkStatus(e?.response?.data?.error || 'Could not make the test link. Please try again.');
    }
  };

  const copyTestLink = async () => {
    if (!boothTestQueueUrl) return;
    try {
      await navigator.clipboard.writeText(boothTestQueueUrl);
      setTestLinkStatus(`Copied the test link: ${boothTestQueueUrl}`);
    } catch (e) {
      window.prompt('Copy to clipboard: Ctrl+C, Enter', boothTestQueueUrl);
      setTestLinkStatus('Could not copy automatically. The link was shown so you can copy it yourself.');
    }
  };

  const copyQueueLink = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      setQueueLinkCopyStatus(`Copied the ${label} link: ${url}`);
    } catch (e) {
      window.prompt('Copy to clipboard: Ctrl+C, Enter', url);
      setQueueLinkCopyStatus('Could not copy automatically. The link was shown so you can copy it yourself.');
    }
  };

  // Check user role before rendering - all hooks must be called first
  const allowedRoles = ['Admin', 'AdminEvent', 'GlobalSupport'];
  const orgBoothLimit = Number(user?.organizationId?.limits?.maxBooths || 0);
  const orgBoothLimitReached = orgBoothLimit > 0 && boothsTotalCount >= orgBoothLimit;
  if (!user || !allowedRoles.includes(user.role)) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="dashboard">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      {/* Video Upload Progress Modal */}
      <VideoUploadProgress 
        progress={uploadProgress}
        fileName={uploadingFile}
        isUploading={isUploading}
      />
      
      <AdminHeader />

      <div className="dashboard-layout">
        <AdminSidebar active="booths" />

        <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
          <div className="dashboard-content">
            <PageInstructionBanner screen={boothMode === 'list' ? 'booth-management' : 'create-edit-page'} />
            <div className="bm-header">
              <h1>Booth Management</h1>
              <div className="bm-header-actions">
                {boothMode === 'list' ? (
                  <>
                    {selectedBooths.length > 0 && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '12px' }}>
                          <input
                            type="checkbox"
                            id="select-all-booths"
                            checked={selectedBooths.length > 0 && selectedBooths.length === paginatedBooths.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all rows on current page
                                if (gridRef.current) {
                                  const pageData = paginatedBooths;
                                  gridRef.current.selectRows(Array.from({ length: pageData.length }, (_, i) => i));
                                  // Manually update state to ensure checkbox reflects selection immediately
                                  setTimeout(() => {
                                    const currentSelection = getSelectedBoothsFromGrid();
                                    setSelectedBooths(currentSelection);
                                  }, 100);
                                }
                              } else {
                                // Deselect all rows
                                if (gridRef.current) {
                                  gridRef.current.clearSelection();
                                  setSelectedBooths([]);
                                }
                              }
                            }}
                            style={{ 
                              width: '18px', 
                              height: '18px', 
                              cursor: 'pointer',
                              accentColor: '#000000'
                            }}
                          />
                          <label htmlFor="select-all-booths" style={{ cursor: 'pointer', userSelect: 'none', fontSize: '14px', fontWeight: '500' }}>
                            Select All
                          </label>
                        </div>
                        <ButtonComponent 
                          cssClass="e-danger"
                          onClick={handleBulkDelete}
                          disabled={isDeleting}
                          aria-label={`Delete ${selectedBooths.length} selected booths`}
                        >
                          {isDeleting ? 'Deleting...' : `Delete Selected (${selectedBooths.length})`}
                        </ButtonComponent>
                      </>
                    )}
                    <ButtonComponent
                      cssClass="e-primary"
                      onClick={() => { setQueueLinkCopyStatus(''); setViewEmployerLinkError(''); setBoothMode('create'); }}
                      disabled={orgBoothLimitReached}
                      title={orgBoothLimitReached ? `Booth limit reached (${orgBoothLimit})` : 'Create Booth'}
                    >
                      Create Booth
                    </ButtonComponent>
                    {orgBoothLimitReached && (
                      <span style={{ fontSize: '12px', color: '#b91c1c', marginLeft: '8px' }}>
                        Booth limit reached ({boothsTotalCount}/{orgBoothLimit})
                      </span>
                    )}
                  </>
                ) : (
                  <ButtonComponent cssClass="e-outline e-primary" onClick={() => { clearBoothDraft(); setBoothMode('list'); }}>
                    Back to List
                  </ButtonComponent>
                )}
              </div>
            </div>

            {boothMode === 'list' ? (
              <div className="bm-grid-wrap" style={{ position: 'relative' }}>
                <div className="form-row bm-search-row">
                  {/* Search Section - Right Aligned */}
                  <div className="bm-search-row-inner">
                    <input
                      ref={searchInputRef}
                      id="booth-search-input"
                      type="text"
                      defaultValue={activeSearchQuery}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                      placeholder="Search by name, event, or any field..."
                      style={{ minWidth: '250px', maxWidth: '400px' }}
                      className="bm-search-input-native"
                    />
                    <ButtonComponent
                      cssClass="e-primary bm-search-button"
                      onClick={handleSearch}
                      disabled={loadingBooths}
                      aria-label="Search booths"
                    >
                      Search
                    </ButtonComponent>
                    {activeSearchQuery && (
                      <ButtonComponent
                        cssClass="e-outline e-primary bm-search-button"
                        onClick={handleClearSearch}
                        disabled={loadingBooths}
                        aria-label="Clear search"
                      >
                        Clear
                      </ButtonComponent>
                    )}
                  </div>
                </div>
                {loadingBooths && (
                  <div className="bm-grid-loading-overlay">
                    <div className="bm-loading-container">
                      <div className="bm-loading-spinner" aria-label="Loading booths" role="status" aria-live="polite"></div>
                      <div className="bm-loading-text">Loading booths...</div>
                    </div>
                  </div>
                )}
                <GridComponent
                  ref={gridRef}
                  dataSource={paginatedBooths}
                  allowPaging={false}
                  allowSorting={true}
                  allowFiltering={true}
                  filterSettings={SYNC_GRID_FILTER_SETTINGS}
                  showColumnMenu={true}
                  showColumnChooser={true}
                  enableHeaderFocus={false}
                  allowResizing={true}
                  allowReordering={true}
                  toolbar={['ColumnChooser']}
                  selectionSettings={{ type: 'Multiple', checkboxOnly: true }}
                  enableHover={true}
                  allowRowDragAndDrop={false}
                >
                  <ColumnsDirective>
                    <ColumnDirective {...SYNC_GRID_CHECKBOX_COLUMN_PROPS} />
                    <ColumnDirective
                      field='name'
                      headerText='Booth Name'
                      width='200'
                      freeze='Left'
                      type='string'
                      allowFiltering={true}
                      template={(props) => (
                        <div style={{
                          wordWrap: 'break-word',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '1.5',
                          padding: '4px 0'
                        }}>
                          {props.name || '-'}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field='logo'
                      headerText='Logo'
                      width='100'
                      textAlign='Center'
                      type='string'
                      allowFiltering={false}
                      template={(props) => props.logo ? <img src={props.logo} alt="Booth logo" style={{ width: 80, height: 28, objectFit: 'contain', borderRadius: 4 }} /> : '-'}
                    />
                    <ColumnDirective
                      field='eventName'
                      headerText='Event Title'
                      width='200'
                      type='string'
                      allowFiltering={true}
                      filter={eventColumnFilter}
                      template={(props) => (
                        <div style={{
                          wordWrap: 'break-word',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '1.5',
                          padding: '4px 0'
                        }}>
                          {props.eventName || 'No events'}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field='recruitersCount'
                      headerText='Recruiters'
                      width='120'
                      textAlign='Center'
                      allowFiltering={true}
                      template={(props) => (
                        <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center' }}>
                          {props.recruitersCount ?? 0}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field='customInviteSlug'
                      headerText='Booth Link'
                      width='180'
                      allowFiltering={true}
                      template={(props) => (
                        <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0' }}>
                          {props.customInviteSlug ? `/queue/${props.customInviteSlug}` : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not set</span>}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field='openTime'
                      headerText='Booth Open'
                      width='180'
                      allowFiltering={true}
                      template={(props) => renderDateTimeCell(props.openTime, 'Not set')}
                    />
                    <ColumnDirective
                      field='recruiterEndTime'
                      headerText='Recruiter End Time'
                      width='190'
                      allowFiltering={true}
                      template={(props) => renderDateTimeCell(props.recruiterEndTime, 'Not set')}
                    />
                    <ColumnDirective
                      field='closeTime'
                      headerText='Booth Close'
                      width='180'
                      allowFiltering={true}
                      template={(props) => renderDateTimeCell(props.closeTime, 'Not set')}
                    />
                    <ColumnDirective
                      field='expireLinkTime'
                      headerText='Expire Date'
                      width='180'
                      allowFiltering={true}
                      template={(props) => renderDateTimeCell(props.expireLinkTime, 'No expiry')}
                    />
                    <ColumnDirective
                      field='companyPage'
                      headerText='Company Page'
                      width='180'
                      minWidth='170'
                      allowSorting={false}
                      allowFiltering={false}
                      clipMode='EllipsisWithTooltip'
                      template={companyPageTemplate}
                    />
                    <ColumnDirective
                      headerText='Actions'
                      width='500'
                      allowSorting={false}
                      allowFiltering={false}
                      template={actionsTemplate}
                    />
                  </ColumnsDirective>
                  <GridInject services={[Sort, Filter, GridToolbar, Selection, Resize, Reorder, ColumnChooser, ColumnMenu, Freeze]} />
                </GridComponent>

                {/* Custom Pagination Footer */}
                {booths.length > 0 && (
                    <div className="custom-pagination" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        backgroundColor: '#f9fafb',
                        borderTop: '1px solid #e5e7eb',
                        marginTop: '0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '14px', color: '#374151' }}>
                                Rows per page:
                            </span>
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    const newSize = parseInt(e.target.value);
                                    setPageSize(newSize);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '14px', color: '#374151' }}>
                                Page {currentPage} of {Math.ceil(booths.length / pageSize) || 1} ({booths.length} total)
                            </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                                onClick={() => {
                                    if (currentPage > 1) {
                                        setCurrentPage(1);
                                    }
                                }}
                                disabled={currentPage <= 1 || loadingBooths}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    backgroundColor: currentPage <= 1 ? '#f3f4f6' : '#fff',
                                    cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    color: currentPage <= 1 ? '#9ca3af' : '#374151'
                                }}
                                title="First Page"
                            >
                                ⟨⟨
                            </button>
                            <button
                                onClick={() => {
                                    if (currentPage > 1) {
                                        setCurrentPage(currentPage - 1);
                                    }
                                }}
                                disabled={currentPage <= 1 || loadingBooths}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    backgroundColor: currentPage <= 1 ? '#f3f4f6' : '#fff',
                                    cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    color: currentPage <= 1 ? '#9ca3af' : '#374151'
                                }}
                                title="Previous Page"
                            >
                                ⟨ Prev
                            </button>
                            
                            <input
                                type="number"
                                min="1"
                                max={Math.ceil(booths.length / pageSize) || 1}
                                value={currentPage}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    const maxPage = Math.ceil(booths.length / pageSize) || 1;
                                    if (val >= 1 && val <= maxPage) {
                                        setCurrentPage(val);
                                    }
                                }}
                                style={{
                                    width: '60px',
                                    padding: '6px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    fontSize: '14px',
                                    textAlign: 'center'
                                }}
                            />
                            
                            <button
                                onClick={() => {
                                    const maxPage = Math.ceil(booths.length / pageSize) || 1;
                                    if (currentPage < maxPage) {
                                        setCurrentPage(currentPage + 1);
                                    }
                                }}
                                disabled={currentPage >= (Math.ceil(booths.length / pageSize) || 1) || loadingBooths}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    backgroundColor: currentPage >= (Math.ceil(booths.length / pageSize) || 1) ? '#f3f4f6' : '#fff',
                                    cursor: currentPage >= (Math.ceil(booths.length / pageSize) || 1) ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    color: currentPage >= (Math.ceil(booths.length / pageSize) || 1) ? '#9ca3af' : '#374151'
                                }}
                                title="Next Page"
                            >
                                Next ⟩
                            </button>
                            <button
                                onClick={() => {
                                    const maxPage = Math.ceil(booths.length / pageSize) || 1;
                                    if (currentPage < maxPage) {
                                        setCurrentPage(maxPage);
                                    }
                                }}
                                disabled={currentPage >= (Math.ceil(booths.length / pageSize) || 1) || loadingBooths}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    backgroundColor: currentPage >= (Math.ceil(booths.length / pageSize) || 1) ? '#f3f4f6' : '#fff',
                                    cursor: currentPage >= (Math.ceil(booths.length / pageSize) || 1) ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    color: currentPage >= (Math.ceil(booths.length / pageSize) || 1) ? '#9ca3af' : '#374151'
                                }}
                                title="Last Page"
                            >
                                ⟩⟩
                            </button>
                        </div>
                    </div>
                )}
              </div>
            ) : (
              <form className="account-form" onSubmit={handleCreateBooth} style={{ maxWidth: 720, paddingBottom: 350 }}>
                <Input
                  label="Booth Name"
                  value={boothForm.boothName}
                  onChange={(e) => setBoothField('boothName', e.target.value)}
                  required
                  placeholder="Enter booth name"
                />

                <div className="form-group">
                  <label className="form-label">Booth Logo</label>
                  <div className="upload-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <ButtonComponent 
                      cssClass="e-outline e-primary e-small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        document.getElementById('booth-logo-upload')?.click();
                      }}
                    >
                      Choose file
                    </ButtonComponent>
                    <input
                      id="booth-logo-upload"
                      type="file"
                      accept={LOGO_ACCEPT}
                      onChange={(e) => onPickBoothLogo(e.target.files?.[0])}
                      style={{ display: 'none' }}
                    />
                    {boothForm.boothLogo && <img src={boothForm.boothLogo} alt="Booth logo" style={{ height: 40, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: 4 }} />}
                  </div>
                </div>

                <Input
                  label="Booth Logo Alt Text (for screen readers)"
                  type="text"
                  value={boothForm.boothLogoAlt}
                  onChange={(e) => setBoothField('boothLogoAlt', e.target.value)}
                  placeholder="e.g., Company Name Logo"
                  maxLength="200"
                />

                <div className="form-group">
                  <label className="form-label">Waiting Area Content</label>
                  <div style={{ marginBottom: '12px' }}>
                    <label className="form-label" htmlFor="waiting-area-mode-switch">Waiting Area Type</label>
                    <button
                      id="waiting-area-mode-switch"
                      type="button"
                      role="switch"
                      aria-checked={boothForm.waitingAreaMode === 'employerPage'}
                      onClick={toggleWaitingAreaMode}
                      style={{
                        width: '100%',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        background: '#fff',
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                    >
                      <span style={{ fontSize: '16px', color: '#111827' }}>
                        {boothForm.waitingAreaMode === 'employerPage' ? 'Employer Page' : '3 Placeholders'}
                      </span>
                      <span
                        aria-hidden="true"
                        style={{
                          width: '48px',
                          height: '26px',
                          borderRadius: '9999px',
                          background: boothForm.waitingAreaMode === 'employerPage' ? '#2563eb' : '#d1d5db',
                          position: 'relative',
                          transition: 'background 0.15s ease'
                        }}
                      >
                        <span
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            background: '#fff',
                            position: 'absolute',
                            top: '2px',
                            left: boothForm.waitingAreaMode === 'employerPage' ? '24px' : '2px',
                            transition: 'left 0.15s ease'
                          }}
                        />
                      </span>
                    </button>
                  </div>
                  {/* Keep both panels mounted so toggling modes never resets editor state. */}
                  <div
                    className="bm-waiting-area-panel"
                    hidden={boothForm.waitingAreaMode !== 'employerPage'}
                    style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#f8fafc', padding: 12 }}
                  >
                    <p style={{ margin: '0 0 10px 0', color: '#374151', fontSize: 14 }}>
                      Configure the employer page in the visual template editor with inline section editing and live preview.
                    </p>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <ButtonComponent cssClass="e-primary" type="button" onClick={openEmployerTemplateEditor}>
                        Open Template Editor
                      </ButtonComponent>
                      <span style={{ fontSize: 13, color: '#6b7280' }}>
                        Sections configured: {(boothForm.employerPageSections || []).filter(s => (s.contentData && Object.keys(s.contentData).some(k => s.contentData[k])) || (s.contentHtml || '').trim()).length}/{(boothForm.employerPageSections || []).length || 8}
                      </span>
                    </div>
                  </div>
                  <div
                    className="bm-rte-tabs bm-waiting-area-panel"
                    hidden={boothForm.waitingAreaMode === 'employerPage'}
                  >
                    <div className="bm-rte-block">
                      <h4>First Placeholder</h4>
                      <RTE
                        ref={rteFirstRef}
                        value={boothForm.firstHtml}
                        change={(e) => setBoothField('firstHtml', e?.value || '')}
                        toolbarSettings={rteToolbarSettings}
                        quickToolbarSettings={RTE_QUICK_TOOLBAR_SETTINGS}
                        insertImageSettings={getInsertImageSettings()}
                        insertVideoSettings={getInsertVideoSettings()}
                        insertAudioSettings={getInsertAudioSettings()}
                        height={550}
                        placeholder="Enter content for first placeholder..."
                        enableXhtml={true}
                        keyDown={handleRteKeyDown}
                        fileUploading={(args) => handleFileUploading(args, rteFirstRef)}
                        imageUploading={handleImageUploading}
                        imageUploadSuccess={handleImageUploadSuccess}
                      >
                        <RTEInject services={[HtmlEditor, RTEToolbar, QuickToolbar, RteLink, RteImage, Table, Video, Audio, EmojiPicker, PasteCleanup, Count, RTEResize, FormatPainter]} />
                      </RTE>
                    </div>
                    <div className="bm-rte-block">
                      <h4>Second Placeholder</h4>
                      <RTE
                        ref={rteSecondRef}
                        value={boothForm.secondHtml}
                        change={(e) => setBoothField('secondHtml', e?.value || '')}
                        toolbarSettings={rteToolbarSettings}
                        quickToolbarSettings={RTE_QUICK_TOOLBAR_SETTINGS}
                        insertImageSettings={getInsertImageSettings()}
                        insertVideoSettings={getInsertVideoSettings()}
                        insertAudioSettings={getInsertAudioSettings()}
                        height={550}
                        placeholder="Enter content for second placeholder..."
                        enableXhtml={true}
                        keyDown={handleRteKeyDown}
                        fileUploading={(args) => handleFileUploading(args, rteSecondRef)}
                        imageUploading={handleImageUploading}
                        imageUploadSuccess={handleImageUploadSuccess}
                      >
                        <RTEInject services={[HtmlEditor, RTEToolbar, QuickToolbar, RteLink, RteImage, Table, Video, Audio, EmojiPicker, PasteCleanup, Count, RTEResize, FormatPainter]} />
                      </RTE>
                    </div>
                    <div className="bm-rte-block">
                      <h4>Third Placeholder</h4>
                      <RTE
                        ref={rteThirdRef}
                        value={boothForm.thirdHtml}
                        change={(e) => setBoothField('thirdHtml', e?.value || '')}
                        toolbarSettings={rteToolbarSettings}
                        quickToolbarSettings={RTE_QUICK_TOOLBAR_SETTINGS}
                        insertImageSettings={getInsertImageSettings()}
                        insertVideoSettings={getInsertVideoSettings()}
                        insertAudioSettings={getInsertAudioSettings()}
                        height={550}
                        placeholder="Enter content for third placeholder..."
                        enableXhtml={true}
                        keyDown={handleRteKeyDown}
                        fileUploading={(args) => handleFileUploading(args, rteThirdRef)}
                        imageUploading={handleImageUploading}
                        imageUploadSuccess={handleImageUploadSuccess}
                      >
                        <RTEInject services={[HtmlEditor, RTEToolbar, QuickToolbar, RteLink, RteImage, Table, Video, Audio, EmojiPicker, PasteCleanup, Count, RTEResize, FormatPainter]} />
                      </RTE>
                    </div>
                  </div>
                </div>

                <MultiSelect
                  label="Select Event (Optional)"
                  value={boothForm.eventIds}
                  onChange={(e) => setBoothField('eventIds', e.target.value)}
                  options={eventOptions}
                  placeholder={loadingEvents ? 'Loading events…' : 'Choose your Event'}
                  name="eventIds"
                />

                <Input
                  label="Recruiters Count"
                  type="number"
                  min="1"
                  value={boothForm.recruitersCount}
                  onChange={(e) => setBoothField('recruitersCount', Number(e.target.value))}
                  required
                  placeholder="Enter number of recruiters"
                />

                <div className="form-inline-row" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <Checkbox
                    label="Enable Expiry Link Time"
                    checked={boothForm.enableExpiry}
                    onChange={(e) => setBoothField('enableExpiry', e.target.checked)}
                    name="enableExpiry"
                    hint="Turn on to make the invite link expire at the time set below. When off, the link never expires."
                  />
                  <DateTimePicker
                    label="Expire Link Time"
                    value={boothForm.expireLinkTime}
                    onChange={(e) => setBoothField('expireLinkTime', e.target.value)}
                    placeholder="Select expiry"
                    disabled={!boothForm.enableExpiry}
                    name="expireLinkTime"
                    hint="Date and time after which the booth invite link stops working. Enable the checkbox above to set an expiry."
                  />
                </div>

                <fieldset className="booth-availability-times" style={{ border: '1px solid #d0d5dd', borderRadius: '8px', padding: '1rem 1.25rem', margin: '0 0 1rem' }}>
                  <legend style={{ padding: '0 0.35rem', fontWeight: 600 }}>Booth Availability times</legend>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <DateTimePicker
                      label="Booth open time"
                      value={boothForm.openTime}
                      onChange={(e) => setBoothField('openTime', e.target.value)}
                      placeholder="Select open time"
                      name="openTime"
                      hint="When the booth opens for the event day. Optional."
                    />
                    <DateTimePicker
                      label="Recruiter end time"
                      value={boothForm.recruiterEndTime}
                      onChange={(e) => setBoothField('recruiterEndTime', e.target.value)}
                      placeholder="Select recruiter end time"
                      name="recruiterEndTime"
                      hint="After this time, job seekers in the waiting area see the message below."
                    />
                    <TextArea
                      label="Recruiter unavailable message"
                      value={boothForm.recruiterUnavailableMessage}
                      onChange={(e) => setBoothField('recruiterUnavailableMessage', e.target.value)}
                      name="recruiterUnavailableMessage"
                      rows={3}
                      placeholder={DEFAULT_RECRUITER_UNAVAILABLE_MESSAGE}
                      hint="Shown on the job seeker waiting area after recruiter end time, while the booth is still open. Leave blank to use the default message."
                    />
                    <DateTimePicker
                      label="Booth close time"
                      value={boothForm.closeTime}
                      onChange={(e) => setBoothField('closeTime', e.target.value)}
                      placeholder="Select close time"
                      name="closeTime"
                      hint="When the booth availability window ends. Optional."
                    />
                  </div>
                </fieldset>

                <OpenPositionsEditor
                  positions={boothForm.openPositions}
                  onChange={(positions) => setBoothField('openPositions', positions)}
                />

                <Input
                  label="Company Page"
                  type="url"
                  value={boothForm.companyPage}
                  onChange={(e) => setBoothField('companyPage', e.target.value)}
                  placeholder="https://example.com"
                  hint="The destination URL opened when a job seeker selects the company logo on the event page. Typically the organization's official website or careers page."
                />

                <Input
                  label="Booth link name"
                  name="customInviteText"
                  value={boothForm.customInviteText}
                  onChange={(e) => setBoothField('customInviteText', e.target.value)}
                  placeholder={boothForm.savedInviteSlug || normalizeInviteCode(boothForm.boothName) || 'e.g. amazon'}
                  hint={`The end of this booth's queue link, for example amazon gives /queue/amazon. Letters and numbers only; spaces become dashes. ${editingBoothId
                    ? 'Leave empty to keep the current link. If you change it, links you have already shared may stop working.'
                    : 'Leave empty to make it from the booth name. If another booth already uses that name, a number is added when you save.'}`}
                />

                <fieldset className="bm-queue-links" aria-labelledby="job-seeker-queue-links-legend">
                  <legend id="job-seeker-queue-links-legend">
                    {boothQueueLinks.length > 1 ? 'Job Seeker Queue Links' : 'Job Seeker Queue Link'}
                  </legend>
                  <p className="form-hint" style={{ marginTop: 0 }}>
                    {boothQueueLinks.length > 1
                      ? 'This booth is in more than one event. "All events" sends each job seeker to the event they are registered for, so it is the one to share in most cases. Use an event\'s own link to send job seekers to that event only.'
                      : 'Share this link to invite job seekers to this booth\'s queue. It stays the same if you rename the booth or add events.'}
                  </p>
                  {boothQueueLinks.length === 0 ? (
                    <p className="form-hint">Enter a booth name to see the link.</p>
                  ) : (
                    <div className="bm-queue-links-list">
                      {boothQueueLinks.map((link) => (
                        <div
                          key={link.key}
                          className="bm-queue-link-row"
                        >
                          <div className="bm-queue-link-controls">
                            <Input
                              label={link.label}
                              value={link.url}
                              readOnly
                              name={`jobSeekerQueueLink-${link.key}`}
                              id={`jobSeekerQueueLink-${link.key}`}
                              className="bm-queue-link-field"
                            />
                            <ButtonComponent
                              cssClass="e-outline e-primary e-small"
                              type="button"
                              aria-label={`Copy ${link.label} link`}
                              onClick={(e) => {
                                e.preventDefault();
                                copyQueueLink(link.url, link.label);
                              }}
                            >
                              <MdLink aria-hidden="true" style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                              Copy
                            </ButtonComponent>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="form-hint bm-queue-link-status" role="status">{queueLinkCopyStatus}</p>
                </fieldset>

                <fieldset className="bm-queue-links" aria-labelledby="test-queue-link-legend" aria-describedby="test-queue-link-hint">
                  <legend id="test-queue-link-legend">Test Queue Link</legend>
                  <p className="form-hint" id="test-queue-link-hint" style={{ marginTop: 0 }}>
                    Use this link to test the booth before the job fair. Anyone who opens it can join this
                    booth's queue even if the booth is not open yet, has closed, or its link has expired.
                    Everything else works as normal: they must be registered for the event, and recruiters,
                    the job seeker and the video call all show a "Test" tag. Share it only with the people
                    helping you test.
                  </p>
                  {!editingBoothId ? (
                    <p className="form-hint">Save the booth first, then edit it to make its test link.</p>
                  ) : testLink.loading && !testLink.code ? (
                    <p className="form-hint">Loading the test link…</p>
                  ) : testLink.code ? (
                    <div className="bm-queue-links-list">
                      <div className="bm-queue-link-row">
                        <div className="bm-queue-link-controls">
                          <Input
                            label="Test link"
                            value={boothTestQueueUrl}
                            readOnly
                            name="boothTestQueueLink"
                            id="boothTestQueueLink"
                            className="bm-queue-link-field"
                          />
                          <ButtonComponent
                            cssClass="e-outline e-primary e-small"
                            type="button"
                            aria-label="Copy test link"
                            onClick={(e) => {
                              e.preventDefault();
                              copyTestLink();
                            }}
                          >
                            <MdLink aria-hidden="true" style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                            Copy
                          </ButtonComponent>
                          <ButtonComponent
                            cssClass="e-outline e-small"
                            type="button"
                            disabled={testLink.loading}
                            aria-describedby="test-queue-link-replace-hint"
                            onClick={(e) => {
                              e.preventDefault();
                              makeTestLink();
                            }}
                          >
                            Replace test link
                          </ButtonComponent>
                        </div>
                        <p className="form-hint" id="test-queue-link-replace-hint">
                          Replace makes a new test link and stops the old one working. Use it if the link was
                          shared with people who should not have it.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bm-queue-links-list">
                      <div>
                        <ButtonComponent
                          cssClass="e-primary e-small"
                          type="button"
                          disabled={testLink.loading}
                          onClick={(e) => {
                            e.preventDefault();
                            makeTestLink();
                          }}
                        >
                          Create test link
                        </ButtonComponent>
                      </div>
                    </div>
                  )}
                  <p className="form-hint bm-queue-link-status" role="status">{testLinkStatus}</p>
                </fieldset>

                <Input
                  label="Join Booth Button Link"
                  type="url"
                  value={boothForm.joinBoothButtonLink}
                  onChange={(e) => setBoothField('joinBoothButtonLink', e.target.value)}
                  placeholder="Leave empty to use Job Seeker Queue Link"
                  name="joinBoothButtonLink"
                  hint={'Optional. If set, the "Join Queue" button on the event page redirects to this URL instead of the default Job Seeker Queue Link.'}
                />

                <Input
                  ref={viewEmployerLinkInputRef}
                  label="View Employer Button Link"
                  // Text, not url: a page on this site (/…) is allowed and the browser's url check would block it.
                  type="text"
                  inputMode="url"
                  spellCheck={false}
                  value={boothForm.viewEmployerButtonLink}
                  onChange={(e) => {
                    setBoothField('viewEmployerButtonLink', e.target.value);
                    if (viewEmployerLinkError) setViewEmployerLinkError('');
                  }}
                  placeholder="Leave empty to use the employer page"
                  name="viewEmployerButtonLink"
                  id="viewEmployerButtonLink"
                  error={viewEmployerLinkError}
                  hint={'Optional. If set, "View Employer" on the event page, the job seeker\'s registered event page and the home page opens this link instead of the booth\'s employer page. Use a full web address such as https://careers.example.com, or a page on this site that starts with /. The Join Queue button is not affected. Leave empty to use the employer page.'}
                />

                <ButtonComponent
                  cssClass="e-primary"
                  disabled={boothSaving}
                  isPrimary={true}
                  onClick={(e) => { e.preventDefault(); handleCreateBooth(e); }}
                >
                  {boothSaving ? 'Saving…' : (editingBoothId ? 'Update Booth' : 'Create Booth')}
                </ButtonComponent>
              </form>
            )}
          </div>
        </main>
      </div>

      {/* Placeholder preview modal - rendered via portal to avoid DOM conflicts */}
      {previewBooth && typeof document !== 'undefined' && createPortal(
        <div 
          role="dialog" 
          aria-modal="true" 
          className="modal-overlay" 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            background: 'rgba(0,0,0,0.4)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 60 
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setPreviewBooth(null);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setPreviewBooth(null);
            }
          }}
          tabIndex={-1}
        >
          <div 
            className="modal-card" 
            style={{ 
              background: '#fff', 
              borderRadius: 8, 
              padding: 20, 
              width: '90%', 
              maxWidth: 1100, 
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                {previewBooth.waitingAreaMode === 'employerPage' ? 'Employer Page Preview' : 'Placeholder Preview'} - {previewBooth.name}
              </h3>
              <ButtonComponent cssClass="e-outline e-primary" onClick={() => setPreviewBooth(null)}>
                Close
              </ButtonComponent>
            </div>
            {previewBooth.waitingAreaMode === 'employerPage' ? (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <EmployerPageTemplate booth={previewBooth} />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                {[0, 1, 2].map(i => (
                  <div 
                    key={`placeholder-${i}-${previewBooth.id || previewBooth.name}`} 
                    style={{ 
                      border: '1px solid #e5e7eb', 
                      borderRadius: 8, 
                      padding: 12, 
                      minHeight: 500, 
                      overflow: 'auto' 
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: hydrateStreamMediaUrls(previewBooth.richSections?.[i]?.contentHtml || '<em>No content</em>') }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirm modal - Syncfusion DialogComponent */}
      <DialogComponent
        ref={deleteDialogRef}
        width="450px"
        isModal={true}
        showCloseIcon={true}
        visible={confirmOpen}
        header="Delete Booth"
        closeOnEscape={true}
        close={cancelDelete}
        cssClass="bm-delete-dialog"
        buttons={[
          {
            buttonModel: {
              content: 'Cancel',
              isPrimary: false,
              cssClass: 'e-outline e-primary'
            },
            click: () => {
              cancelDelete();
            }
          },
          {
            buttonModel: {
              content: boothSaving ? 'Deleting…' : 'Delete',
              isPrimary: true,
              cssClass: 'e-danger'
            },
            click: () => {
              confirmDelete();
            }
          }
        ]}
      >
        <p style={{ margin: 0, lineHeight: '1.5' }}>
          Are you sure you want to delete <strong>{rowPendingDelete?.name}</strong>? This action cannot be undone.
        </p>
      </DialogComponent>

      {/* Bulk Delete confirm modal - Syncfusion DialogComponent */}
      <DialogComponent
        width="450px"
        isModal={true}
        showCloseIcon={true}
        visible={confirmBulkDeleteOpen}
        header="Bulk Delete Booths"
        closeOnEscape={true}
        close={cancelBulkDelete}
        cssClass="bm-delete-dialog"
        buttons={[
          {
            buttonModel: {
              content: 'Cancel',
              isPrimary: false,
              cssClass: 'e-outline e-primary'
            },
            click: () => {
              cancelBulkDelete();
            }
          },
          {
            buttonModel: {
              content: isDeleting ? 'Deleting...' : 'Delete',
              isPrimary: true,
              cssClass: 'e-danger'
            },
            click: () => {
              confirmBulkDelete();
            }
          }
        ]}
      >
        <p style={{ margin: 0, lineHeight: '1.5' }}>
          Are you sure you want to permanently delete <strong>{selectedBooths.length} booth(s)</strong>? This action cannot be undone.
        </p>
      </DialogComponent>

      {/* Syncfusion ToastComponent */}
      <ToastComponent
        ref={(toast) => toastRef.current = toast}
        position={{ X: 'Right', Y: 'Bottom' }}
        showProgressBar={true}
        timeOut={3000}
        newestOnTop={true}
      />

    </div>
  );
}
